import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshIcon from "../../../assets/images/icons/refresh-btn.svg";

// Movement before a press counts as a drag rather than a tap. Below this the button
// still fires its refresh, so a slightly unsteady tap does not silently become a
// no-op drag. Same threshold the vehicle card drag uses (useVehicleCardOrder).
const DRAG_THRESHOLD_PX = 4;

// Gap kept between the button and the edges of the calendar area, so a dragged
// button never ends up flush against a border or half under the scrollbar.
const EDGE_MARGIN_PX = 16;

// How long the button stays hidden - after the calendar mounts, so it does not compete
// with the initial month load for attention, and again the moment a refresh is asked
// for, so it is out of the way while the weeks reload.
const REVEAL_DELAY_MS = 5000;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Floating refresh button for the month calendar.
 *
 * Tap refreshes the weeks currently on screen; press and move to reposition it.
 * The position lives in component state only, so it returns to its default corner
 * on every visit - deliberate, not an oversight.
 */
const CalendarRefreshButton = ({ onRefresh, isRefreshing = false, boundsRef }) => {
    const { t } = useTranslation();
    const buttonRef = useRef(null);

    // Hidden for REVEAL_DELAY_MS after mount and after every refresh. Rendered as null
    // rather than hidden with CSS so there is no invisible click target and nothing for
    // keyboard or screen reader users to land on while it is away.
    const [isRevealed, setIsRevealed] = useState(false);

    // Bumped to restart the hide timer. A counter rather than a boolean because two
    // refreshes in a row must each restart it, and setting a flag that is already set
    // would not re-run the effect.
    const [hideCycle, setHideCycle] = useState(0);

    // null = "not yet placed", which renders at the CSS default corner. Once dragged
    // it becomes explicit VIEWPORT pixels: the button is position:fixed so that it
    // stays put while the calendar scrolls underneath it, and fixed coordinates are
    // viewport coordinates. The bounds element only supplies the limits.
    const [position, setPosition] = useState(null);

    // Drag bookkeeping. Refs, not state: these change on every pointermove and must
    // not re-render the calendar underneath.
    const dragRef = useRef({
        pointerId: null,
        startX: 0,
        startY: 0,
        originLeft: 0,
        originTop: 0,
        moved: false,
    });

    const getBoundsRect = useCallback(() => {
        const boundsEl = boundsRef?.current;
        return boundsEl ? boundsEl.getBoundingClientRect() : null;
    }, [boundsRef]);

    // Keep the button inside its container. Called while dragging and again on
    // resize, so shrinking the window can never strand it off-screen.
    const clampToBounds = useCallback((left, top) => {
        const bounds = getBoundsRect();
        const button = buttonRef.current;

        if (!bounds || !button) {
            return { left, top };
        }

        const { width, height } = button.getBoundingClientRect();

        const minLeft = bounds.left + EDGE_MARGIN_PX;
        const maxLeft = bounds.right - width - EDGE_MARGIN_PX;
        const minTop = bounds.top + EDGE_MARGIN_PX;
        const maxTop = bounds.bottom - height - EDGE_MARGIN_PX;

        return {
            left: clamp(left, minLeft, Math.max(minLeft, maxLeft)),
            top: clamp(top, minTop, Math.max(minTop, maxTop)),
        };
    }, [getBoundsRect]);

    const endDrag = useCallback(() => {
        dragRef.current.pointerId = null;
    }, []);

    const handlePointerDown = useCallback((event) => {
        // Primary button / single touch only; let anything else behave normally.
        if (event.button !== 0 && event.pointerType === "mouse") {
            return;
        }

        const bounds = getBoundsRect();
        const button = buttonRef.current;
        if (!bounds || !button) {
            return;
        }

        const rect = button.getBoundingClientRect();
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            // Viewport coordinates, to match position:fixed.
            originLeft: rect.left,
            originTop: rect.top,
            moved: false,
        };
    }, [getBoundsRect]);

    useEffect(() => {
        const handlePointerMove = (event) => {
            const drag = dragRef.current;
            if (drag.pointerId === null || event.pointerId !== drag.pointerId) {
                return;
            }

            const deltaX = event.clientX - drag.startX;
            const deltaY = event.clientY - drag.startY;

            if (!drag.moved && Math.abs(deltaX) < DRAG_THRESHOLD_PX && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
                return;
            }

            drag.moved = true;
            // Stops the press turning into a text selection or a page scroll once it
            // is clearly a drag.
            event.preventDefault();

            setPosition(clampToBounds(drag.originLeft + deltaX, drag.originTop + deltaY));
        };

        const handlePointerUp = (event) => {
            const drag = dragRef.current;
            if (drag.pointerId === null || event.pointerId !== drag.pointerId) {
                return;
            }
            endDrag();
        };

        const handleKeyDown = (event) => {
            // Escape abandons a drag in progress, matching the vehicle card drag.
            if (event.key === "Escape" && dragRef.current.pointerId !== null) {
                endDrag();
            }
        };

        window.addEventListener("pointermove", handlePointerMove, { passive: false });
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerUp);
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerUp);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [clampToBounds, endDrag]);

    // A window resize can leave a dragged button outside the (now smaller) calendar.
    useEffect(() => {
        const handleResize = () => {
            setPosition((prev) => (prev ? clampToBounds(prev.left, prev.top) : prev));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [clampToBounds]);

    // One path for both the mount and every refresh: hide now, come back after the delay.
    useEffect(() => {
        setIsRevealed(false);
        const revealTimer = window.setTimeout(() => setIsRevealed(true), REVEAL_DELAY_MS);
        return () => window.clearTimeout(revealTimer);
    }, [hideCycle]);

    const handleClick = useCallback((event) => {
        // The press that ended a drag must not also refresh.
        if (dragRef.current.moved) {
            dragRef.current.moved = false;
            event.preventDefault();
            return;
        }

        if (!isRefreshing) {
            onRefresh?.();
            // Get out of the way immediately. The refresh itself is not awaited: the
            // delay is fixed, so a refresh that runs longer simply finishes with the
            // button already back and still disabled by isRefreshing.
            setHideCycle((cycle) => cycle + 1);
        }
    }, [isRefreshing, onRefresh]);

    // Reuses the existing `refresh` key, which is already translated in all ten
    // locales. A new key would have to go through Lokalise first and would render
    // as the raw key string in nine languages until it did.
    const label = t("refresh");

    // After every hook, never before - an early return above them would change the
    // hook call order between renders.
    if (!isRevealed) {
        return null;
    }

    return (
        <button
            type="button"
            ref={buttonRef}
            className={`calendar-refresh-fab${isRefreshing ? " is-refreshing" : ""}`}
            style={position ? { left: `${position.left}px`, top: `${position.top}px`, right: "auto", bottom: "auto" } : undefined}
            onPointerDown={handlePointerDown}
            onClick={handleClick}
            disabled={isRefreshing}
            aria-label={label}
            title={label}
        >
            <img src={RefreshIcon} alt="" aria-hidden="true" className="calendar-refresh-fab__icon" />
        </button>
    );
};

export default CalendarRefreshButton;
